import { useCallback, useMemo } from 'react';

import { type AirActionTrackingLocation, useTrackClickedCollectContent } from '@air/analytics';
import { useAirModal } from '@air/provider-modal';

import { ContentCollectionModal } from '~/components/Modals/ContentCollectionModal/ContentCollectionModal';
import { type ImportOptions } from '~/constants/MenuOptions';
import { useDraftCanvas } from '~/hooks/canvases/useDraftCanvas';
import { useCreateSubBoard } from '~/hooks/useCreateSubBoard';
import { useCurrentBoardPermissions } from '~/hooks/useCurrentBoardPermissions';
import { useExposeImportSources } from '~/hooks/useExposeImportSources';
import { useImportFrom } from '~/hooks/useImportFrom';
import { useStoragePlanRestriction } from '~/hooks/useStoragePlanRestriction';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { useUploadFilesFromComputer } from '~/hooks/useUploadFilesFromComputer';
import { useUploadFoldersFromComputer } from '~/hooks/useUploadFoldersFromComputer';
import { centralizedBoardSelector } from '~/store/centralizedBoard/selectors';
import { getAddMenuOptions } from '~/utils/dropdownMenuOptions/getAddMenuOptions';
import { getActionMenuOptions } from '~/utils/menuOptions/getActionMenuOptions';
import { canCreateAsset } from '~/utils/permissions/assetPermissions';
import {
  canCreateBoard,
  canCreateCanvasInBoard,
  canSeeBoardContentCollectionForm,
} from '~/utils/permissions/boardPermissions';
import { useAirStore } from '~/utils/ReduxUtils';

export const usePrivateBoardActions = ({ trackLocation }: { trackLocation: AirActionTrackingLocation }) => {
  const { uploadFilesFromComputer } = useUploadFilesFromComputer({ trackLocation });
  const { uploadFoldersFromComputer } = useUploadFoldersFromComputer({ trackLocation });
  const { importFrom } = useImportFrom({ trackLocation });
  const { createSubBoard } = useCreateSubBoard();
  const { boardPermissions } = useCurrentBoardPermissions();
  const store = useAirStore();
  const { openDraftCanvas } = useDraftCanvas();
  const { generativeCanvasEnabled } = useTemporaryFeatureFlags();
  const { exposeImportSources } = useExposeImportSources();
  const { isStoragePlanRestricted, showStoragePlanRestrictionModal } = useStoragePlanRestriction();

  const canAddSubBoard = canCreateBoard(boardPermissions);
  const canAddAssets = canCreateAsset(boardPermissions);
  const canAddCanvas = canCreateCanvasInBoard(boardPermissions);
  const canManageLinks = canSeeBoardContentCollectionForm(boardPermissions);

  const [showContentCollectionModal] = useAirModal(ContentCollectionModal);

  const { trackClickedCollectContent } = useTrackClickedCollectContent();

  const onCollectContentClick = useCallback(() => {
    const currentBoard = centralizedBoardSelector(store.getState());

    if (!currentBoard) {
      return;
    }
    if (isStoragePlanRestricted) {
      showStoragePlanRestrictionModal();
    } else {
      trackClickedCollectContent();
      showContentCollectionModal({ boardId: currentBoard?.id, boardTitle: currentBoard.title });
    }
  }, [
    showContentCollectionModal,
    store,
    trackClickedCollectContent,
    isStoragePlanRestricted,
    showStoragePlanRestrictionModal,
  ]);

  const onAddSubBoardClick = useCallback(() => {
    createSubBoard({ trackLocation });
  }, [createSubBoard, trackLocation]);

  const onImportFromClick = useCallback(
    (importOption: ImportOptions) => {
      if (isStoragePlanRestricted) {
        showStoragePlanRestrictionModal();
      } else {
        importFrom(importOption);
      }
    },
    [importFrom, isStoragePlanRestricted, showStoragePlanRestrictionModal],
  );

  const onUploadFilesFromComputerClick = useCallback(() => {
    if (isStoragePlanRestricted) {
      showStoragePlanRestrictionModal();
    } else {
      uploadFilesFromComputer();
    }
  }, [uploadFilesFromComputer, isStoragePlanRestricted, showStoragePlanRestrictionModal]);

  const onUploadFoldersFromComputerClick = useCallback(() => {
    if (isStoragePlanRestricted) {
      showStoragePlanRestrictionModal();
    } else {
      uploadFoldersFromComputer();
    }
  }, [uploadFoldersFromComputer, isStoragePlanRestricted, showStoragePlanRestrictionModal]);

  const onCreateCanvasClick = useCallback(() => {
    const currentBoard = centralizedBoardSelector(store.getState());

    if (!currentBoard) {
      return;
    }
    openDraftCanvas({
      boardId: currentBoard.id,
      analyticsContext: { source: 'board', board_id: currentBoard.id },
    });
  }, [openDraftCanvas, store]);

  const canShowCreateCanvas = generativeCanvasEnabled && canAddCanvas;

  const privateBoardActionsOptions = useMemo(
    () =>
      getAddMenuOptions({
        onAddSubBoard: canAddSubBoard ? onAddSubBoardClick : undefined,
        onCreateCanvas: canShowCreateCanvas ? onCreateCanvasClick : undefined,
        onImportFrom: canAddAssets && canAddSubBoard ? onImportFromClick : undefined,
        onUploadFilesFromComputer: canAddAssets ? onUploadFilesFromComputerClick : undefined,
        onUploadFoldersFromComputer: canAddAssets && canAddSubBoard ? onUploadFoldersFromComputerClick : undefined,
        onCollectContent: canManageLinks ? onCollectContentClick : undefined,
        exposeImportSources,
      }),
    [
      canAddAssets,
      canAddSubBoard,
      canShowCreateCanvas,
      canManageLinks,
      onAddSubBoardClick,
      onCreateCanvasClick,
      onImportFromClick,
      onUploadFilesFromComputerClick,
      onUploadFoldersFromComputerClick,
      exposeImportSources,
      onCollectContentClick,
    ],
  );

  const options = useMemo(
    () =>
      getActionMenuOptions({
        onAddSubBoard: canAddSubBoard ? onAddSubBoardClick : undefined,
        onCreateCanvas: canShowCreateCanvas ? onCreateCanvasClick : undefined,
        onImportFrom: canAddAssets && canAddSubBoard ? onImportFromClick : undefined,
        onUploadFilesFromComputer: canAddAssets ? onUploadFilesFromComputerClick : undefined,
        onUploadFoldersFromComputer: canAddAssets && canAddSubBoard ? onUploadFoldersFromComputerClick : undefined,
        onCollectContent: canManageLinks ? onCollectContentClick : undefined,
        exposeImportSources,
      }),
    [
      canAddAssets,
      canAddSubBoard,
      canShowCreateCanvas,
      canManageLinks,
      onAddSubBoardClick,
      onCreateCanvasClick,
      onImportFromClick,
      onUploadFilesFromComputerClick,
      onUploadFoldersFromComputerClick,
      onCollectContentClick,
      exposeImportSources,
    ],
  );

  return {
    privateBoardActionsOptions,
    options,
  };
};
